import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  connectionsQueryOptions,
  useCreateOrder,
  vendorsQueryOptions,
} from "@/lib/queries"
import { createOrderErrorMessage } from "@/features/marketplace/lib/order-errors"
import { connectionNeedsResign } from "@/features/marketplace/lib/terms-compliance"
import { ConnectWizard } from "@/features/marketplace/components/ConnectWizard/ConnectWizard"
import { ReSignSheet } from "@/features/marketplace/components/ReSignSheet"
import { useConnectWizardStore } from "@/features/marketplace/store/connectWizard.store"
import { usePendingSendStore } from "@/features/marketplace/store/pendingSend.store"
import type {
  SentScriptsMap,
  TenantVendorConnection,
  Vendor,
} from "@/features/marketplace/types/marketplace.types"
import { SendSuccessView } from "./SendSuccessView"
import { SendAlreadySentView } from "./SendAlreadySentView"
import {
  SendVendorOptionCard,
  SendVendorOptionCardSkeleton,
  type SendVendorOptionStatus,
} from "./SendVendorOptionCard"

const SUCCESS_AUTO_CLOSE_MS = 5000
const VENDOR_LIST_MAX_HEIGHT = "max-h-[min(320px,50dvh)]"

interface SuccessInfo {
  vendorName: string
  count: number
}

interface VendorOption {
  vendor: Vendor
  connection: TenantVendorConnection | null
  status: SendVendorOptionStatus
}

export interface SendToVendorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All script IDs the user intends to send (may include already-sent). */
  scriptIds: string[]
  sentScriptsMap: SentScriptsMap
  tenantId: string
}

function resolveVendorStatus(
  vendor: Vendor,
  connection: TenantVendorConnection | null,
): SendVendorOptionStatus {
  if (!connection || connection.disconnectedAt !== null) return "not-connected"
  if (!vendor.isActive) return "inactive"
  if (connectionNeedsResign(connection)) return "needs-resign"
  return "connected"
}

function buildVendorOptions(
  vendors: Vendor[],
  connections: TenantVendorConnection[],
): VendorOption[] {
  const connectionByVendorId = new Map(
    connections
      .filter((c) => c.disconnectedAt === null)
      .map((c) => [c.vendorId, c] as const),
  )

  // Keep inactive vendors in the list so resolveVendorStatus can mark them
  // "inactive" (the card renders a disabled badge) and a pending intent that
  // targets a now-inactive vendor isn't silently dropped.
  return vendors.map((vendor) => {
    const connection = connectionByVendorId.get(vendor.id) ?? null
    return {
      vendor,
      connection,
      status: resolveVendorStatus(vendor, connection),
    }
  })
}

export function SendToVendorDialog({
  open,
  onOpenChange,
  scriptIds,
  sentScriptsMap,
  tenantId,
}: SendToVendorDialogProps) {
  const { t } = useTranslation()
  const [selectedVendorId, setSelectedVendorId] = React.useState("")
  const [resignOpen, setResignOpen] = React.useState(false)
  const [connectOpen, setConnectOpen] = React.useState(false)
  const [successInfo, setSuccessInfo] = React.useState<SuccessInfo | null>(null)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const successHandledRef = React.useRef(false)
  // Guards the connection-id → vendor-id hydration so a connections refetch
  // can't clobber a selection the user (or the open-reset) already made.
  const pendingHydratedRef = React.useRef(false)

  const pendingIntent = usePendingSendStore((s) => s.intent)
  const setPendingIntent = usePendingSendStore((s) => s.setIntent)
  const clearPendingIntent = usePendingSendStore((s) => s.clearIntent)
  const openWizard = useConnectWizardStore((s) => s.openWizard)
  const closeWizard = useConnectWizardStore((s) => s.closeWizard)

  const vendorsQuery = useQuery(vendorsQueryOptions(tenantId))
  const connectionsQuery = useQuery(connectionsQueryOptions(tenantId))
  const connections = connectionsQuery.data ?? []
  const vendorOptions = buildVendorOptions(vendorsQuery.data ?? [], connections)

  const createOrder = useCreateOrder()

  const alreadySentIds = scriptIds.filter((id) => sentScriptsMap[id] != null)
  const unsentIds = scriptIds.filter((id) => sentScriptsMap[id] == null)
  const allAlreadySent = scriptIds.length > 0 && unsentIds.length === 0

  const selectedOption = vendorOptions.find((o) => o.vendor.id === selectedVendorId)
  const selectedConnection = selectedOption?.connection ?? null
  const selectedNeedsResign = selectedOption?.status === "needs-resign"
  const selectedNotConnected = selectedOption?.status === "not-connected"
  const selectedCanSend = selectedOption?.status === "connected"

  const isLoading = vendorsQuery.isLoading || connectionsQuery.isLoading
  const loadFailed = vendorsQuery.isError || connectionsQuery.isError

  React.useEffect(() => {
    if (!open) return
    setSuccessInfo(null)
    setSendError(null)
    successHandledRef.current = false
    pendingHydratedRef.current = false
    createOrder.reset()

    const sameTenant = pendingIntent?.tenantId === tenantId
    if (sameTenant && pendingIntent?.vendorId && !pendingIntent.tenantVendorId) {
      setSelectedVendorId(pendingIntent.vendorId)
      pendingHydratedRef.current = true
    } else if (!(sameTenant && pendingIntent?.tenantVendorId)) {
      // No connection-id to resolve — selection is final here.
      setSelectedVendorId("")
      pendingHydratedRef.current = true
    }
    // The tenantVendorId case needs `connections`, which may not have loaded
    // yet; the hydration effect below resolves it once they arrive.
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, pendingIntent?.vendorId]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open || pendingHydratedRef.current) return
    if (pendingIntent?.tenantId !== tenantId || !pendingIntent.tenantVendorId) return
    const conn = connections.find((c) => c.id === pendingIntent.tenantVendorId)
    if (conn) {
      setSelectedVendorId(conn.vendorId)
      pendingHydratedRef.current = true
    }
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, connections])

  React.useEffect(() => {
    if (
      selectedVendorId &&
      vendorOptions.length > 0 &&
      !vendorOptions.some((o) => o.vendor.id === selectedVendorId)
    ) {
      setSelectedVendorId("")
    }
  }, [selectedVendorId, vendorOptions])

  function submitOrder(connectionId: string, ids: string[]) {
    setSendError(null)
    createOrder.mutate(
      { tenantId, tenantVendorId: connectionId, scriptIds: ids },
      {
        onSuccess: () => {
          const conn = connections.find((c) => c.id === connectionId)
          const vendorName = conn?.vendor.name ?? t("opportunities.scripts.sendDialog.vendorLabel")
          successHandledRef.current = false
          setSuccessInfo({ vendorName, count: ids.length })
        },
        onError: (err) => {
          setSendError(createOrderErrorMessage(err, t))
        },
      },
    )
  }

  function handleSuccessDone() {
    if (successHandledRef.current || !successInfo) {
      onOpenChange(false)
      return
    }
    successHandledRef.current = true
    toast.success(
      successInfo.count === 1
        ? t("opportunities.scripts.sendDialog.successToast_one", { vendorName: successInfo.vendorName })
        : t("opportunities.scripts.sendDialog.successToast_other", {
            count: successInfo.count,
            vendorName: successInfo.vendorName,
          }),
    )
    clearPendingIntent()
    onOpenChange(false)
  }

  function handleSend() {
    if (!selectedOption || unsentIds.length === 0) return

    if (selectedNotConnected) {
      setPendingIntent({ tenantId, scriptIds: unsentIds, vendorId: selectedOption.vendor.id })
      openWizard(selectedOption.vendor.id)
      setConnectOpen(true)
      return
    }

    if (!selectedConnection) return

    if (selectedNeedsResign) {
      setPendingIntent({
        tenantId,
        tenantVendorId: selectedConnection.id,
        scriptIds: unsentIds,
      })
      setResignOpen(true)
      return
    }

    submitOrder(selectedConnection.id, unsentIds)
  }

  function handleConnectSuccess(connection: TenantVendorConnection) {
    const intent = usePendingSendStore.getState().intent
    const ids = intent?.scriptIds ?? unsentIds
    setConnectOpen(false)
    closeWizard()
    clearPendingIntent()
    submitOrder(connection.id, ids)
  }

  function handleConnectClose() {
    setConnectOpen(false)
    closeWizard()
  }

  function handleResignSuccess() {
    const intent = usePendingSendStore.getState().intent
    if (!intent?.tenantVendorId || intent.tenantId !== tenantId) return
    setResignOpen(false)
    submitOrder(intent.tenantVendorId, intent.scriptIds)
  }

  function handleResignClose() {
    setResignOpen(false)
  }

  function handleDialogClose(nextOpen: boolean) {
    if (nextOpen) return
    if (successInfo) {
      handleSuccessDone()
      return
    }
    if (createOrder.isPending) return
    if (!resignOpen && !connectOpen) clearPendingIntent()
    onOpenChange(false)
  }

  function handleRetryLoad() {
    void vendorsQuery.refetch()
    void connectionsQuery.refetch()
  }

  React.useEffect(() => {
    if (!open) {
      setResignOpen(false)
      setConnectOpen(false)
    }
  }, [open])

  const dialogHidden = resignOpen || connectOpen

  return (
    <>
      <Dialog open={open && !dialogHidden} onOpenChange={handleDialogClose}>
        <DialogContent className="overflow-hidden sm:max-w-lg">
          {successInfo ? (
            <SendSuccessView
              vendorName={successInfo.vendorName}
              count={successInfo.count}
              autoCloseMs={SUCCESS_AUTO_CLOSE_MS}
              onDone={handleSuccessDone}
            />
          ) : allAlreadySent ? (
            <SendAlreadySentView
              scriptIds={scriptIds}
              sentScriptsMap={sentScriptsMap}
              onClose={() => onOpenChange(false)}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">
                  {t("opportunities.scripts.sendDialog.title")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    `opportunities.scripts.sendDialog.description_${unsentIds.length === 1 ? "one" : "other"}`,
                    { count: unsentIds.length },
                  )}
                </DialogDescription>
              </DialogHeader>

              {loadFailed ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="alert-circle" size="sm" className="mx-auto mb-2 text-destructive/60" />
                  <p className="text-sm font-medium text-foreground">
                    {vendorsQuery.isError
                      ? t("opportunities.scripts.sendDialog.vendorsFailed")
                      : t("opportunities.scripts.sendDialog.connectionsFailed")}
                  </p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={handleRetryLoad}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : isLoading ? (
                <div className="flex flex-col gap-2">
                  <SendVendorOptionCardSkeleton />
                  <SendVendorOptionCardSkeleton />
                </div>
              ) : vendorOptions.length === 0 ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="building-2" size="sm" className="mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-sm font-medium text-foreground">
                    {t("opportunities.scripts.sendDialog.noVendors")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("opportunities.scripts.sendDialog.noVendorsHint")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {sendError && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-destructive" />
                      <p className="text-xs text-foreground/80">{sendError}</p>
                    </div>
                  )}

                  {alreadySentIds.length > 0 && !allAlreadySent && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t(
                          alreadySentIds.length === 1
                            ? "opportunities.scripts.sendDialog.alreadySentWarning_one"
                            : "opportunities.scripts.sendDialog.alreadySentWarning_other",
                          { count: alreadySentIds.length },
                        )}
                      </p>
                    </div>
                  )}

                  {selectedNeedsResign && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t("marketplace.resign.sendBlocked")}
                      </p>
                    </div>
                  )}

                  {!allAlreadySent && (
                    <div
                      className={`flex flex-col gap-2 overflow-y-auto pr-0.5 ${VENDOR_LIST_MAX_HEIGHT}`}
                      role="listbox"
                      aria-label={t("opportunities.scripts.sendDialog.vendorLabel")}
                    >
                      {vendorOptions.map((option) => (
                        <SendVendorOptionCard
                          key={option.vendor.id}
                          vendorName={option.vendor.name}
                          logoUrl={option.connection?.vendor.logoUrl ?? option.vendor.logoUrl}
                          description={option.vendor.description}
                          selected={selectedVendorId === option.vendor.id}
                          status={option.status}
                          onSelect={() => setSelectedVendorId(option.vendor.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="gap-2.5">
                {loadFailed || vendorOptions.length === 0 ? (
                  <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenChange(false)}
                      disabled={createOrder.isPending}
                    >
                      {t("common.cancel")}
                    </Button>
                    {!allAlreadySent && selectedVendorId && (
                      <Button
                        size="sm"
                        onClick={handleSend}
                        disabled={
                          createOrder.isPending ||
                          selectedOption?.status === "inactive" ||
                          (!selectedCanSend && !selectedNotConnected && !selectedNeedsResign)
                        }
                        iconStart={
                          createOrder.isPending
                            ? "loader"
                            : selectedNeedsResign
                              ? "file-text"
                              : selectedNotConnected
                                ? "link"
                                : "upload"
                        }
                        className={createOrder.isPending ? "[&_svg]:animate-spin" : ""}
                      >
                        {createOrder.isPending
                          ? t("opportunities.scripts.sendDialog.confirming")
                          : selectedNeedsResign
                            ? t("marketplace.resign.action")
                            : selectedNotConnected
                              ? t("opportunities.scripts.sendDialog.connectAndSend")
                              : t(
                                  `opportunities.scripts.sendDialog.confirm_${unsentIds.length === 1 ? "one" : "other"}`,
                                )}
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedOption && connectOpen && (
        <ConnectWizard
          vendorId={selectedOption.vendor.id}
          tenantId={tenantId}
          open={connectOpen}
          onClose={handleConnectClose}
          onConnectSuccess={handleConnectSuccess}
        />
      )}

      {selectedConnection && (
        <ReSignSheet
          connectionId={selectedConnection.id}
          vendorId={selectedConnection.vendorId}
          tenantId={tenantId}
          open={resignOpen}
          onClose={handleResignClose}
          onSuccess={handleResignSuccess}
        />
      )}
    </>
  )
}
